import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import apiClient from '../api/client';
import { pendingReturnPath, storeGrant } from '../utils/hubSession';
import { usePlatformName } from '../contexts/PlatformBrandingContext';

/**
 * Where the platform sends someone back after they confirmed an action there with its password
 * (utils/hubSession.js, confirmThroughPlatform): `?token=`. The token becomes a short grant for
 * that action, and they return to the page that asked, which finds it and carries on.
 */
export default function ConfirmActionPage() {
  const platformName = usePlatformName();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get('token');
    // The token must not stay in the address bar or the history
    window.history.replaceState(null, '', '/confirm');
    if (!token) {
      setError('This link does not carry a confirmation. Start again from the page you were on.');
      return;
    }
    apiClient
      .post('/sso/confirm', { token })
      .then(({ data }) => {
        if (!storeGrant(data.action, data.confirmation)) {
          setError(
            'The page that asked for this confirmation is no longer waiting for it. Start again from there.'
          );
          return;
        }
        navigate(pendingReturnPath(), { replace: true });
      })
      .catch((e) =>
        setError(e.response?.data?.error || 'The confirmation could not be checked. Try again.')
      );
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Not confirmed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => navigate(pendingReturnPath(), { replace: true })}
              className="block w-full px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg"
            >
              Back
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-accent-gold" />
            <p className="mt-3 text-gray-600">Checking your confirmation from {platformName}…</p>
          </>
        )}
      </div>
    </div>
  );
}
